import React from 'react';
import { useI18n } from '../../context/I18nContext';
import CablePreview from '../visualization/CablePreview';
import { EW3035_MARKING_COMPENSATION_DEFAULTS } from '../../machines/EW-3035+L';

const WORD_SIZE_VALUES = Object.keys(EW3035_MARKING_COMPENSATION_DEFAULTS).map(Number);

const ToggleRow = ({ title, description, checked, onChange, color = 'bg-blue-600' }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
    <div>
      <h5 className="text-sm font-medium text-gray-800 mb-1">{title}</h5>
      {description && <p className="text-xs text-gray-600">{description}</p>}
    </div>
    <label className="flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />
      <span className={`relative w-11 h-6 rounded-full transition-colors ${checked ? color : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </span>
    </label>
  </div>
);

const NumberField = ({ label, value, onChange, min = 0, max, step = 1 }) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
    <input
      type="number"
      value={value ?? ''}
      onChange={(event) => onChange(parseInt(event.target.value, 10) || 0)}
      min={min}
      max={max}
      step={step}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
    />
  </label>
);

const EW3035AdvancedSettingsPanel = ({ settings, setSettings }) => {
  const { t } = useI18n();
  const previewSettings = {
    lunghezzaPreview: 350,
    ...settings
  };
  const update = (patch) => setSettings(prev => ({ ...prev, ...patch }));

  return (
    <div className="relative space-y-6 pr-[440px]">
      <div className="fixed right-8 top-24 z-40 w-[400px] max-h-[calc(100vh-8rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-xl">
        <CablePreview settings={previewSettings} setSettings={setSettings} />
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.markingConfiguration')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            label={t('settings.markingHeadPosition')}
            value={settings.posMarcaturaTesta}
            onChange={(value) => update({ posMarcaturaTesta: value })}
          />
          <NumberField
            label={t('settings.markingTailPosition')}
            value={settings.posMarcaturaCoda}
            onChange={(value) => update({ posMarcaturaCoda: value })}
          />
        </div>

        <div className="border-t border-gray-200 pt-4 mt-5">
          <h4 className="text-sm font-medium text-gray-700 mb-3">{t('settings.secondaryMarkings')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ToggleRow
              title={t('settings.enableHead2Tail2')}
              checked={!!settings.abilitaMarcature2}
              onChange={(checked) => update({ abilitaMarcature2: checked })}
            />
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">{t('settings.secondaryMarkingContent')}</span>
              <select
                value={settings.tipoMarcature2}
                onChange={(event) => update({ tipoMarcature2: event.target.value })}
                disabled={!settings.abilitaMarcature2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="collegamento">{t('settings.connection')}</option>
                <option value="siglatura">{t('settings.signature')}</option>
              </select>
            </label>
            <NumberField
              label={t('settings.additionalDistance')}
              value={settings.distanzaMarcature2}
              onChange={(value) => update({ distanzaMarcature2: value })}
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mt-5">
          <h4 className="text-sm font-medium text-gray-700 mb-3">{t('settings.flipMarkings')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ['ribaltaTesta', t('settings.head')],
              ['ribaltaCentrale', t('settings.central')],
              ['ribaltaCoda', t('settings.tail')]
            ].map(([key, label]) => (
              <ToggleRow
                key={key}
                title={label}
                checked={settings[key] === 1}
                onChange={(checked) => update({ [key]: checked ? 1 : 0 })}
              />
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mt-5">
          <h4 className="text-sm font-medium text-gray-700">{t('settings.markingCompensationByWordSize')}</h4>
          <p className="mt-1 text-xs text-gray-600">{t('settings.markingCompensationByWordSizeDesc')}</p>
          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">WordSize</th>
                  <th className="px-4 py-2 text-left font-medium">{t('settings.compensationMmPerCharacter')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {WORD_SIZE_VALUES.map(wordSize => {
                  const compensation = {
                    ...EW3035_MARKING_COMPENSATION_DEFAULTS,
                    ...(settings.compensazioneMarcaturaPerWordSize || {})
                  };

                  return (
                    <tr key={wordSize} className="bg-white">
                      <td className="px-4 py-2 font-medium text-gray-800">{wordSize}</td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          aria-label={`WordSize ${wordSize}`}
                          value={compensation[wordSize]}
                          min="-10"
                          max="10"
                          step="0.1"
                          onChange={(event) => {
                            const parsedValue = Number.parseFloat(event.target.value);
                            update({
                              compensazioneMarcaturaPerWordSize: {
                                ...compensation,
                                [wordSize]: Number.isFinite(parsedValue) ? parsedValue : 0
                              }
                            });
                          }}
                          className="w-28 rounded-md border border-gray-300 px-3 py-1.5 focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.centralMarkingAndLoop')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">{t('settings.centralMarkingType')}</span>
            <select
              value={settings.marcaturaCentrale}
              onChange={(event) => update({ marcaturaCentrale: event.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="niente">{t('settings.nothing')}</option>
              <option value="siglatura">{t('settings.signature')}</option>
              <option value="numerazione">{t('settings.numbering')}</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">{t('settings.loopMode')}</span>
            <select
              value={settings.loopMode}
              onChange={(event) => update({ loopMode: event.target.value })}
              disabled={settings.marcaturaCentrale === 'niente'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="numero">{t('settings.basedOnNumber')}</option>
              <option value="distanza">{t('settings.basedOnDistance')}</option>
            </select>
          </label>
          <NumberField
            label={settings.loopMode === 'numero' ? t('settings.loopNumber') : t('settings.loopDistance')}
            value={settings.loopMode === 'numero' ? settings.loopNum : settings.loopDist}
            onChange={(value) => update(settings.loopMode === 'numero' ? { loopNum: value } : { loopDist: value })}
            step={settings.loopMode === 'numero' ? 1 : 20}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.otherSettings')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            label={t('settings.boldSize')}
            value={settings.grandezzaGrassetto}
            onChange={(value) => update({ grandezzaGrassetto: value || 1 })}
            min={1}
            max={10}
          />
          <ToggleRow
            title={t('settings.lengthCompensation')}
            description={t('settings.lengthCompensationDesc')}
            checked={!!settings.compensazioneLunghezza}
            onChange={(checked) => update({ compensazioneLunghezza: checked })}
            color="bg-amber-600"
          />
          <ToggleRow
            title={t('settings.forceBlackCables')}
            description={t('settings.forceBlackCablesDesc')}
            checked={!!settings.forceBlackCables}
            onChange={(checked) => update({ forceBlackCables: checked })}
            color="bg-gray-900"
          />
          <ToggleRow
            title={t('settings.reducePeelManual')}
            description={t('settings.reducePeelManualDesc')}
            checked={!!settings.reducePeelForManualMarking}
            onChange={(checked) => update({ reducePeelForManualMarking: checked })}
            color="bg-amber-600"
          />
        </div>
      </div>
    </div>
  );
};

export default EW3035AdvancedSettingsPanel;
